import { useTranslation } from 'react-i18next';
import { LockErrorList } from '@/accessControl/LockErrorList';
import { LockErrorSharedReasonSummary } from '@/accessControl/LockErrorSharedReasonSummary';
import { LockErrorWorkbookGroupSection } from '@/accessControl/LockErrorWorkbookGroupSection';
import {
  getGroupSharedReason,
  groupLockErrorsByWorkbook,
  hasWorkbookGrouping,
} from '@/accessControl/groupLockErrorsByWorkbook';
import { sqWorkbookStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { ExternalLink, Icon } from '@seeqdev/qomponents';

/**
  Display a list of global items that are preventing a user locking an Analysis or Vantage Room. Underneath each item,
  display a reason why the lock failed and a link to where the item is used.
 */

interface GlobalItemsProps {
  hasLockingFailedPreviouslyDueToGlobalItems: boolean;
}

export const GlobalItems = ({ hasLockingFailedPreviouslyDueToGlobalItems }: GlobalItemsProps) => {
  const { t } = useTranslation();
  const { lockingErrors, isVantage } = useFlux(sqWorkbookStore);

  const cannotLockKey = isVantage ? 'ACCESS_CONTROL.LOCKING.CANNOT_LOCK_VANTAGE' : 'ACCESS_CONTROL.LOCKING.CANNOT_LOCK';
  const globalItemsTooltipKey = isVantage
    ? 'ACCESS_CONTROL.LOCKING.GLOBAL_ITEMS_TOOLTIP_VANTAGE'
    : 'ACCESS_CONTROL.LOCKING.GLOBAL_ITEMS_TOOLTIP';

  const HelpIcon = (
    <ExternalLink href={`${SUPPORT_LINK}/locking-a-workbench-analysis`} variant="icon">
      <Icon
        icon={faQuestionCircle}
        large={true}
        tooltip={t(globalItemsTooltipKey)}
        extraClassNames="text-interactive"
      />
    </ExternalLink>
  );

  const groups = groupLockErrorsByWorkbook(lockingErrors ?? []);
  const isGroupedByWorkbook = hasWorkbookGrouping(groups);
  /**
   * With nothing to group by, everything is one flat list - but that list can still collapse onto a single shared
   * reason exactly like the "Items outside these workbooks" bucket would, since it *is* that bucket, just without the
   * header.
   */
  const ungroupedSharedReason =
    !isGroupedByWorkbook && groups.length === 1 ? getGroupSharedReason(groups[0]) : undefined;

  return (
    <>
      {lockingErrors && lockingErrors.length > 0 && (
        <>
          <div className="tw:pb-sq-10" data-testid="cannotLockTitle">
            {hasLockingFailedPreviouslyDueToGlobalItems && (
              <>
                <strong>{t(cannotLockKey)}</strong> {HelpIcon}
              </>
            )}
            {!hasLockingFailedPreviouslyDueToGlobalItems && (
              <>
                <strong>{t('ACCESS_CONTROL.LOCKING.PREVIOUSLY_ATTEMPT_LOCKED')}</strong> {HelpIcon}
                <div>
                  <strong>{t('ACCESS_CONTROL.LOCKING.REATTEMPT_LOCKING')}</strong>
                </div>
              </>
            )}
          </div>
          <div className="tw:bg-sq-background tw:p-sq-10">
            {isGroupedByWorkbook ? (
              groups.map((group) => (
                <LockErrorWorkbookGroupSection key={group.workbookId} group={group} isVantage={isVantage} />
              ))
            ) : ungroupedSharedReason ? (
              <LockErrorSharedReasonSummary
                errors={lockingErrors}
                sharedReason={ungroupedSharedReason}
                hasWorkbook={false}
                isVantage={isVantage}
                testId="ungrouped-summary"
              />
            ) : (
              <LockErrorList errors={lockingErrors} isVantage={isVantage} />
            )}
          </div>
        </>
      )}
    </>
  );
};
